
import Component from 'vue-class-component';
import Vue, { PropType } from 'vue';
import { Prop, Watch } from 'vue-property-decorator';

import { Debounce } from '../helpers';

interface ITipPosition {
	centerX: number,
	centerY: number,
	X: number,
	Y: number,
	arrowX: number,
	arrowY: number,
	floatingPosition?: number,
}

export type ToolTipPositionProp = 'left' | 'right' | 'top' | 'bottom';

@Component({
	name: 'ToolTip',
	components: {}
})
export default class ToolTip extends Vue {
	/* Props
	========================================*/
	@Prop({ required: false, type: Number, default: 16 })
	arrowSize: number

	@Prop({ required: false, type: String as PropType<ToolTipPositionProp>, default: 'left' })
	position: ToolTipPositionProp;

	@Prop({ required: false, type: Boolean, default: false })
	customMessage: boolean

	@Prop({ required: true, type: String })
	text: string

	@Prop({ required: false, type: Boolean, default: false })
	show: boolean

	@Prop({ required: false, type: Boolean, default: false })
	scroll: boolean

	@Prop({ required: false, type: String, default: '#565656' })
	arrowColor: string

	@Prop({ required: false, type: String })
	border: string

	@Prop({ required: false, type: String })
	width: string

	@Prop({ required: false, type: Boolean, default: false })
	floatingTip: boolean

	/* Refs
	========================================*/
	$refs!: {
		_tippy: HTMLElement
	}


	/* Data
	========================================*/
	tipPosition: ITipPosition|null=null
	positionOverride: null


	/* Computed Properties
	========================================*/
	get tipStyle(): string {
		let style = '';

		if (this.width) style += `max-width: ${this.width};`;
		if (!this.tipPosition) return style;

		switch (this.position) {
			case 'right':
				style += `
					top: ${this.tipPosition.centerY}px;
					right: ${this.tipPosition.X}px;
				`;
				break;
			case 'bottom':
				style += `
					bottom: ${this.tipPosition.Y}px;
					left: ${this.tipPosition.centerX - this.tipPosition.floatingPosition}px;
				`;
				break;
			case 'top':
				style += `
					top: ${this.tipPosition.Y}px;
					left: ${this.tipPosition.centerX - this.tipPosition.floatingPosition}px;
				`;
				break;
			default:
				style += `
					top: ${this.tipPosition.centerY}px;
					left: ${this.tipPosition.X}px;
				`;
				break;
		}

		return style;
	}

	get tipArrowStyle(): string {
		if (!this.tipPosition) return '';

		const middle = Math.floor(this.arrowSize / 2) * -1;

		let style = `
			background: ${this.arrowColor};
			height: ${this.arrowSize}px;
			width: ${this.arrowSize}px;
		`;
		switch (this.position) {
			case 'right':
				style += `
					top: ${this.tipPosition.arrowY}px;
					left: ${middle}px;
				`;
				break;
			case 'bottom':
				style += `
					top: ${middle}px;
					left: ${this.tipPosition.arrowX + this.tipPosition.floatingPosition}px;
				`;
				break;
			case 'top':
				style += `
					bottom: ${middle}px;
					left: ${this.tipPosition.arrowX + this.tipPosition.floatingPosition}px;
				`;
				break;
			default:
				style += `
					top: ${this.tipPosition.arrowY}px;
					right: ${middle}px;
				`;
				break;
		}
		switch (this.border) {
			case 'left top':
				style += `
					border-top: 3px solid rgb(217, 217, 217);
					border-left: 3px solid rgb(217, 217, 217);
				`;
				break;
			case 'right bottom':
				style += `
					border-right: 3px solid rgb(217, 217, 217);
					border-bottom: 3px solid rgb(217, 217, 217);
				`;
				break;
		}
		return style;
	}


	/* Methods
	========================================*/
	calculatePosition() {
		const tip = this.$refs._tippy;
		const tipParent = this.$el;

		if (!tip || !tipParent) return;

		const parentHeight = tipParent.clientHeight;
		const parentWidth = tipParent.clientWidth;
		const tipHeight = tip.clientHeight;
		const tipWidth = tip.clientWidth;

		const tempPosition: ITipPosition = {
			centerY: Math.floor(((parentHeight - tipHeight) / 2)),
			centerX: Math.floor(((parentWidth - tipWidth) / 2)),
			X: (tipWidth + 20) * -1,
			Y: (tipHeight + 10) * -1,
			arrowY: Math.ceil((tipHeight - 16) / 2),
			arrowX: Math.ceil((tipWidth - 10) / 2)
		};

		this.tipPosition = tempPosition;
		this.floatTipContainer();
	}

	floatTipContainer() {
		// If this is a floating tip check if we are overflowing the screen
		//if(this.floatingTip){
		const screenWidth = document.documentElement.clientWidth;

		const { clientWidth } = this.$refs._tippy;
		const relativePosition = this.$el.getBoundingClientRect().x;

		const borderAndPadding = 12;

		let margin = (screenWidth - clientWidth) / 2;

		if (clientWidth < screenWidth / 2) margin = margin * 2;

		// Is this tooltip floating offscreen
		const isOverflowing = (relativePosition + borderAndPadding + clientWidth) > screenWidth;

		if (isOverflowing) {
			this.tipPosition.floatingPosition = this.tipPosition.centerX + relativePosition - margin;
		}
		//}
	}

	scrollToElement() {
		if (!this.scroll) return;

		this.$el.scrollIntoView({
			behavior: 'smooth', // Defines the transition animation.
			block: 'center', // Defines vertical alignment.
		});
	}


	/* Lifecycle Hooks
	========================================*/
	mounted() {
		if (this.show) {
			this.calculatePosition();
		}

		const debouncedResizeListener = Debounce(500, false, () => this.calculatePosition());
		window.addEventListener('resize', debouncedResizeListener);
		this.$on('hook:beforeDestroy', () => {
			window.removeEventListener('resize', debouncedResizeListener);
		});
	}

	/* Watchers
	========================================*/
	@Watch('show')
	showChanged(shouldShow: boolean, wasShowing: boolean) {
		// Only take action when we need to show this tooltip
		if (!shouldShow || wasShowing) return; // shouldShow === wasShowing

		// Allow the rest of the component to be positioned before calculating the tooltip position
		this.$nextTick(() => {
			this.calculatePosition();
			this.scrollToElement();
		});
	}

}
